import { useCallback, useSyncExternalStore } from 'react';
import { readStorage, writeStorage } from '@/lib/storage';

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

/**
 * A small remembered choice (grid/list view, a chosen tab) kept in `localStorage`. Server and first
 * client render both show `fallback`; the stored value takes over right after hydration, and other
 * tabs stay in sync. Values that are not in `allowed` are ignored.
 */
export function useStoredChoice<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const getSnapshot = (): T => {
    const raw = readStorage(key);
    return raw !== null && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
  };
  const value = useSyncExternalStore(subscribe, getSnapshot, () => fallback);
  const set = useCallback(
    (next: T) => {
      writeStorage(key, next);
      for (const listener of listeners) listener();
    },
    [key],
  );
  return [value, set];
}
